/**
 * Registry of the data grids that are currently alive, so the assistant palette can tell which grid the
 * user is actually looking at and what it is filtered to. That RSQL is sent along with a question as
 * "screen context", letting the assistant answer "hoeveel zijn er dit jaar?" against the list already on
 * screen instead of asking what the user means.
 *
 * Every tab stays mounted — `App.vue` only `v-show`s the active one — so "on screen" cannot be answered
 * from the registry alone. It is answered from the DOM: a grid inside a hidden tab has no `offsetParent`.
 * The palette is an overlay that sits on top without hiding what is behind it, so this still holds while
 * the palette is open.
 *
 * A plain module-level list, not a store (like the neighbouring `AssistantGridFilter`): nothing renders
 * from this and no reactivity is needed. It is read once, at the moment a question is asked.
 */
export interface RegisteredGrid {
	/** Backend resource name of the grid, e.g. `sales-order`. A getter — a grid can be re-pointed. */
	resourceKey: () => string;
	/** The grid's root element; used to decide whether this is the grid on screen. */
	element: () => HTMLElement | null | undefined;
	/** The RSQL the grid is showing right now: base criteria + the active column filters. */
	criteria: () => string;
}

/** What the palette needs about the grid on screen. */
export interface ActiveGridContext {
	resourceKey: string;
	criteria: string;
}

const grids: RegisteredGrid[] = [];

/** The grid the user last worked in (filtered or selected). The strongest signal on a split screen. */
let lastTouched: RegisteredGrid | undefined;

export function registerGrid(grid: RegisteredGrid): void {
	if (!grids.includes(grid)) grids.push(grid);
}

export function unregisterGrid(grid: RegisteredGrid): void {
	const index = grids.indexOf(grid);
	if (index !== -1) grids.splice(index, 1);
	if (lastTouched === grid) lastTouched = undefined;
}

/** Mark `grid` as the one the user is working in. Called on a real interaction, not on every render. */
export function touchGrid(grid: RegisteredGrid): void {
	lastTouched = grid;
}

/**
 * The grid on screen, or undefined when none is.
 *
 * `preferResource` is the active tab's own resource, and it outranks "last worked in": a split screen
 * like `sales-order.index.vue` shows an order grid and a row grid at once, and the row grid can steal
 * `lastTouched` from a programmatic selection (`preselectFirstRow`) the user never made. Within the
 * grids that do match, `lastTouched` breaks the tie — two grids of the same resource with different base
 * criteria are told apart by which one the user actually worked in.
 */
export function activeGridContext(preferResource?: string): ActiveGridContext | undefined {
	const visible = grids.filter(isVisible);
	const matching = preferResource ? visible.filter((candidate) => candidate.resourceKey() === preferResource) : [];
	const touched = lastTouched;
	const grid =
		(touched !== undefined && matching.includes(touched) ? touched : undefined) ??
		matching[0] ??
		(touched !== undefined && visible.includes(touched) ? touched : undefined) ??
		visible[0];
	if (!grid) return undefined;

	try {
		return { resourceKey: grid.resourceKey(), criteria: grid.criteria() };
	} catch {
		// A grid mid-teardown throws from its api getter. It is not the one the user is looking at
		// anyway, and a missing context is always better than a wrong one.
		return undefined;
	}
}

function isVisible(grid: RegisteredGrid): boolean {
	const element = grid.element();
	return !!element && element.isConnected && element.offsetParent !== null;
}
